// components/Base/NotificationToast.tsx
import React from "react";
import {
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
} from "./Toast";
import { Bell, CheckCircle, AlertCircle } from "lucide-react";

interface NotificationToastProps {
  type: "info" | "success" | "warning";
  title: string;
  message: string;
  onDismiss?: () => void;
  onAction?: () => void;
  actionText?: string;
}

export const NotificationToast = React.forwardRef<
  React.ElementRef<typeof Toast>,
  NotificationToastProps
>(
  (
    { type, title, message, onDismiss, onAction, actionText, ...props },
    ref
  ) => {
    const icons = {
      info: <Bell className="h-4 w-4" />,
      success: <CheckCircle className="h-4 w-4" />,
      warning: <AlertCircle className="h-4 w-4" />,
    };

    const variants = {
      info: "default" as const,
      success: "success" as const,
      warning: "destructive" as const,
    };

    return (
      <Toast ref={ref} variant={variants[type]} {...props}>
        <div className="flex items-start space-x-3">
          <div className="mt-0.5 flex-shrink-0">{icons[type]}</div>
          <div className="grid flex-1 gap-1">
            <ToastTitle>{title}</ToastTitle>
            <ToastDescription>{message}</ToastDescription>
          </div>
        </div>
        {onAction && actionText && (
          <ToastAction altText={actionText} onClick={onAction}>
            {actionText}
          </ToastAction>
        )}
        <ToastClose onClick={onDismiss} />
      </Toast>
    );
  }
);

NotificationToast.displayName = "NotificationToast";
